<template>
  <section
    id="routeTabs"
    class="multiple-nav-wrap"
  >
    <div class="fixed-tags-wrapper">
      <template
        v-for="element of fixedViewsRoute"
        :key="element.fullPath"
      >
        <router-link
          :to="{ ...element }"
          tag="a"
          :class="[
            'fixed-tags-item',
            isActiveRoute(element) ? 'active' : ''
          ]"
        >
          <div class="title-wrap">
            <span>{{ element.meta.title }}</span>
          </div>
        </router-link>
      </template>
    </div>
    <scroll-pane
      ref="scrollPaneRef"
      class="tags-wrapper"
    >
      <draggable
        :list="multipleNavList"
        item-key="fullPath"
        :animation="500"
        filter=".fixed-item"
        :scroll="true"
        class="tags-list-wrap"
        @end="dataDragEnd"
      >
        <template #item="{ element }">
          <router-link
            :to="{ ...element }"
            tag="a"
            :class="[
              'tags-item',
              isActiveRoute(element) ? 'active' : ''
            ]"
            @mouseenter="currentPath = element.fullPath"
            @mouseleave="currentPath = '-'"
            @dblclick="handleCommand(3)"
          >
            <div class="title-wrap">
              <span>{{ element.meta.title }}</span>

              <span
                v-if="!element.meta.fixed"
                :class="[
                  'btn-close close',
                  setCloseBtnBg && currentPath === element.fullPath ? 'bg' : ''
                ]"
                @mouseenter="setCloseBtnBg = true"
                @mouseleave="setCloseBtnBg = false"
                @click.prevent.stop="closeNavHandle(element)"
              >
                <el-icon><close /></el-icon>
              </span>
            </div>
          </router-link>
        </template>
      </draggable>
    </scroll-pane>
    <section class="multiple-nav-options-wrap">
      <section class="refresh-route-button">
        <span
          class="btn-icon"
          @click="handleCommand(3)"
        >
          <el-icon>
            <refresh />
          </el-icon>
        </span>
      </section>
      <section class="setting-route-button">
        <el-dropdown @command="handleCommand">
          <span class="btn-icon">
            <el-icon>
              <setting />
            </el-icon>
          </span>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                :command="3"
              >
                <span class="dropdown-item__icon-wrap">
                  <el-icon>
                    <refresh />
                  </el-icon>
                </span>
                重新加载
              </el-dropdown-item>
              <el-dropdown-item :command="4">
                <span class="dropdown-item__icon-wrap">
                  <el-icon>
                    <close />
                  </el-icon>
                </span>
                关闭标签页
              </el-dropdown-item>

              <el-dropdown-item
                :command="5"
                divided
              >
                <span class="dropdown-item__icon-wrap">
                  <el-icon>
                    <arrow-left />
                  </el-icon>
                </span>
                关闭左侧标签页
              </el-dropdown-item>
              <el-dropdown-item :command="6">
                <span class="dropdown-item__icon-wrap">
                  <el-icon>
                    <arrow-right />
                  </el-icon>
                </span>
                关闭右侧标签页
              </el-dropdown-item>

              <el-dropdown-item
                :command="1"
                divided
              >
                <span class="dropdown-item__icon-wrap">
                  <el-icon>
                    <remove />
                  </el-icon>
                </span>
                关闭其他标签页
              </el-dropdown-item>
              <el-dropdown-item :command="2">
                <span class="dropdown-item__icon-wrap">
                  <el-icon>
                    <circle-close-filled />
                  </el-icon>
                </span>
                关闭全部标签页
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </section>
    </section>
  </section>
</template>
<script lang="ts">
  import Draggable from 'vuedraggable';
  import {
    Close, Setting, Refresh, Remove, CircleCloseFilled, ArrowLeft, ArrowRight,EditPen
  } from '@element-plus/icons-vue';
  import {
    defineComponent, watch, onMounted, reactive, toRefs, computed, ref, nextTick,
  } from 'vue';
  import { RouteLocationNormalizedLoaded, useRoute, useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import config from '@/config';
  import { RefreshPage, ClosePage } from '@/hooks';
  import {
    ADD_TAG_ONLY,
    ADD_TAG_AND_CACHE,
    SET_VISITED_VIEWS,
    DEL_OTHER_TAG_AND_CACHE,
    DEL_LEFT_TAG_AND_CACHE,
    DEL_RIGHT_TAG_AND_CACHE,
    DEL_ALL_TAG_AND_CACHE,
  } from '@/store/actionTypes';
  import ScrollPane from './ScrollPane.vue';

  interface InterfaceState {
    multipleNavList: TypeObject[],
    currentPath: string,
    setCloseBtnBg: boolean,
    fixedViewsRoute: TypeObject[]
  }

  export default defineComponent({
    name: 'multiple-nav',
    components: {
      Draggable,
      ScrollPane,
      Close,
      Setting,
      Refresh,
      Remove,
      CircleCloseFilled,
      ArrowLeft,
      ArrowRight,
      EditPen,
    },
    setup() {
      const router = useRouter();
      const route = useRoute();
      const { commit, dispatch, getters } = useStore();

      const scrollPaneRef = ref();

      const state:InterfaceState = reactive({
        multipleNavList: [],
        currentPath: '-',
        setCloseBtnBg: false,

        fixedViewsRoute: config.multipleNav.fixedRoute || [],
      });

      const visitedViews = computed(() => getters.visitedViews);

      const addTagHandle = (to:RouteLocationNormalizedLoaded) => {
        if (to.meta && typeof (to.meta.keepAlive) === 'boolean' && to.meta.keepAlive === false) {
          to.meta && to.meta.title && commit(`multipleNav/${ADD_TAG_ONLY}`, to);
        } else {
          to.meta && to.meta.title && dispatch(`multipleNav/${ADD_TAG_AND_CACHE}`, to);
        }
      };

      const setMultipleNavList = (views:TypeObject) => {
        state.multipleNavList = views?.[config.multipleNav.clientId] || [];
      };

      const moveToCurrentTag = () => {
        const navList:any = document.querySelectorAll('.tags-item');
        const scrollPaneContainer:TypeObject = scrollPaneRef.value;
        for (const nav of navList) {
          if (route.fullPath === nav?.pathname) {
            scrollPaneContainer.moveToCurrentTag(nav, navList, scrollPaneContainer.$el.offsetWidth);
            return;
          }
        }
      };

      const isActiveRoute = (routeInfo:TypeObject) => routeInfo.fullPath === route.fullPath;

      const closeNavHandle = (routeInfo:TypeObject) => {
        const curIndex = state.multipleNavList.findIndex((nav) => nav.fullPath === routeInfo.fullPath);
        const preRouteInfo = state.multipleNavList[curIndex - 1] || {};
        const nextRouteInfo = state.multipleNavList[curIndex + 1] || {};
        if (isActiveRoute(routeInfo)) {
          if (preRouteInfo.fullPath) {
            router.push({
              ...preRouteInfo,
            });
          } else if (nextRouteInfo.fullPath) {
            router.push({
              ...nextRouteInfo,
            });
          } else if (state.fixedViewsRoute.length) {
            router.replace(state.fixedViewsRoute[0].fullPath || '');
          } else {
            router.replace('/');
          }
        }
        state.currentPath = '-';
        ClosePage(routeInfo);
      };

      onMounted(() => {
        addTagHandle(route);

        moveToCurrentTag();
      });

      watch(
        route,
        (routeInfo) => {
          addTagHandle(routeInfo);
          nextTick(() => {
            moveToCurrentTag();
          });
        }
      );

      watch(
        visitedViews,
        (views) => {
          setMultipleNavList(views);
        },
        {
          deep: true,
          immediate: true,
        }
      );

      return {
        ...toRefs(state),
        scrollPaneRef,
        isActiveRoute,
        handleCommand: (command: number) => {
          state.currentPath = '-';
          if (command === 1) {
            commit(`multipleNav/${DEL_OTHER_TAG_AND_CACHE}`, route);
          } else if (command === 2) {
            commit(`multipleNav/${DEL_ALL_TAG_AND_CACHE}`);
            if (state.fixedViewsRoute.length) {
              router.replace(state.fixedViewsRoute[0].fullPath || '');
            } else {
              router.replace('/');
            }
          } else if (command === 3) {
            RefreshPage(route);
          } else if (command === 4) {
            closeNavHandle(route);
          } else if (command === 5) {
            commit(`multipleNav/${DEL_LEFT_TAG_AND_CACHE}`, route);
          } else if (command === 6) {
            commit(`multipleNav/${DEL_RIGHT_TAG_AND_CACHE}`, route);
          }
        },

        closeNavHandle,

        dataDragEnd: () => {
          if (state.multipleNavList.length > 0) {
            commit(`multipleNav/${SET_VISITED_VIEWS}`, state.multipleNavList);
          }
        },
      };
    },
  });
</script>
<style lang="scss" scoped>
:deep(.el-dropdown) {
  line-height: normal;
}

.multiple-nav-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  .tags-wrapper {
    flex: 1;
    box-sizing: border-box;

    .tags-list-wrap {
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
    }
  }

  .fixed-tags-item,
  .tags-item {
    position: relative;
    display: flex;
    color: $--color-grey1;
    line-height: 28px;
    background-color: $--color-grey-4;
    border: 1px solid $--color-grey-2;
    border-right-color: $--color-grey-4;
    border-left-color: $--color-grey-4;
    cursor: pointer;

    .title-wrap {
      position: relative;
      display: flex;
      align-items: center;
      padding: 0 $--base-space;
      font-size: 12px;
      white-space: nowrap;
    }

    &.active {
      color: var(--el-color-primary);
      background-color: $--color-white;
      border-right-color: $--color-grey-2;
      border-left-color: $--color-grey-2;

      .title-wrap {
        &:before {
          width: 8px;
        }
      }
    }

    .btn-close {
      display: flex;
      align-items: center;
      justify-content: center;

      &.close {
        width: 16px;
        height: 16px;
        margin-left: 2px;
        border-radius: 50%;

        &.bg {
          background-color: $--color-grey-base;
        }
      }
    }

    &:before {
      position: absolute;
      top: 50%;
      left: 0;
      width: 1px;
      height: 22px;
      margin-top: -11px;
      background-color: $--color-grey-3;
      content: '';
    }

    &:hover {
      background-color: $--color-white;
    }

    &:first-child:before,
    &.active:before,
    &:hover:before {
      width: 0;
    }

    &.active + a:before,
    &:hover + a:before {
      width: 0;
    }

    &:first-child {
      border-left-color: $--color-grey-2;
      border-top-left-radius: 2px;
      border-bottom-left-radius: 2px;
    }

    &:last-child {
      border-right-color: $--color-grey-2;
      border-top-right-radius: 2px;
      border-bottom-right-radius: 2px;
    }
  }

  .multiple-nav-options-wrap {
    position: relative;
    display: flex;
    justify-content: flex-end;
    width: 62px;

    .refresh-route-button,
    .setting-route-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      cursor: pointer;

      .btn-icon {
        display: flex;
        align-items: center;
        font-size: 18px;
      }
    }

    .refresh-route-button {
      &:before {
        position: absolute;
        top: 4px;
        left: 3px;
        width: 1px;
        height: 20px;
        border-left: 1px solid var(--el-border-color);
        content: '';
      }
    }

    .dropdown-item__icon-wrap {
      display: flex;
      font-size: 14px;
    }
  }
}
</style>
